// Based on https://www.npmjs.com/package/fast-deep-equal
// - Converted to TypeScript
// - Remove support for Date and RegExp (not available in JSON)
// - Accept undefined values as same as missing keys
const isArray = Array.isArray;
const keyList = Object.keys;
const hasProp = Object.prototype.hasOwnProperty;
function jsonEqual(a, b) {
    if (a === b)
        return true;
    if (a && b && typeof a === 'object' && typeof b === 'object') {
        const aIsArray = isArray(a);
        const bIsArray = isArray(b);
        if (aIsArray && bIsArray) {
            const length = a.length;
            if (length !== b.length) {
                return false;
            }
            for (let i = length; i-- !== 0;) {
                if (!jsonEqual(a[i], b[i])) {
                    return false;
                }
            }
            return true;
        }
        if (aIsArray !== bIsArray) {
            return false;
        }
        // Strip out keys where the value is undefined (since JSON treats this as a non-existent field)
        const aKeys = keyList(a).filter((key) => a[key] !== undefined);
        const bKeys = keyList(b).filter((key) => b[key] !== undefined);
        const aKeysLength = aKeys.length;
        if (aKeysLength !== bKeys.length) {
            return false;
        }
        for (let i = aKeysLength; i-- !== 0;) {
            if (!hasProp.call(b, aKeys[i])) {
                return false;
            }
        }
        for (let i = aKeysLength; i-- !== 0;) {
            const key = aKeys[i];
            if (!jsonEqual(a[key], b[key])) {
                return false;
            }
        }
        return true;
    }
    return a !== a && b !== b;
}

// Private class used as singleton
class CannotMergeClass {
}
const CannotMerge = new CannotMergeClass();

function combineMergers(...mergers) {
    const combinedMerger = (orig, left, right, basePath = [], mergeFn = combinedMerger) => {
        for (const merger of mergers) {
            const result = merger(orig, left, right, basePath, mergeFn);
            if (result !== CannotMerge) {
                return result;
            }
        }
        // When nesting combineMergers, don't throw
        if (mergeFn !== combinedMerger) {
            return CannotMerge;
        }
        throw new CannotMergeError(basePath);
    };
    return combinedMerger;
}
class CannotMergeError extends Error {
    constructor(path) {
        super(`cannot merge /${path.join('/')}`);
    }
}
function trimergeEquality(orig, left, right) {
    // Merging to same thing or only left changed
    if (left === right || orig === right) {
        return left;
    }
    // Only right changed
    if (orig === left) {
        return right;
    }
    return CannotMerge;
}
function trimergeEqualityCreator(equal) {
    return (orig, left, right) => {
        if (equal(left, right)) {
            // Merging to same thing
            return left;
        }
        if (equal(orig, left)) {
            // Only right changed
            return right;
        }
        if (equal(orig, right)) {
            // Only left changed
            return left;
        }
        return CannotMerge;
    };
}

function type(object) {
    const t = typeof object;
    if (t !== 'object') {
        return t;
    }
    if (object === null) {
        return 'null';
    }
    if (Array.isArray(object)) {
        return 'array';
    }
    const proto = Object.getPrototypeOf(object);
    if (proto === Object.prototype || proto === null) {
        return 'object';
    }
    return 'instance';
}

function jsSameType(orig, left, right) {
    const origType = type(orig);
    const leftType = type(left);
    const rightType = type(right);
    if (leftType !== rightType || leftType !== origType) {
        return CannotMerge;
    }
    return leftType;
}

function trimergeObject(orig = {}, left, right, path, mergeFn) {
    if (jsSameType(orig, left, right) !== 'object') {
        return CannotMerge;
    }
    const origKeys = Object.keys(orig);
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    const mergedObject = {};
    const keys = new Set(origKeys);
    leftKeys.forEach(keys.add, keys);
    rightKeys.forEach(keys.add, keys);
    let leftSame = true;
    let rightSame = true;
    let count = 0;
    keys.forEach((key) => {
        const leftElement = left[key];
        const rightElement = right[key];
        const merged = mergeFn(orig[key], leftElement, rightElement, [...path, key], mergeFn);
        if (leftElement !== merged) {
            leftSame = false;
        }
        if (rightElement !== merged) {
            rightSame = false;
        }
        if (merged !== undefined) {
            count++;
            mergedObject[key] = merged;
        }
    });
    // Check if result is shallow equal to left or right
    if (leftSame && leftKeys.length === count) {
        return left;
    }
    if (rightSame && rightKeys.length === count) {
        return right;
    }
    return mergedObject;
}

class RouteWildCardClass {
}
const RouteWildCard = new RouteWildCardClass();
function routeMergers(...routes) {
    const root = {
        keyMap: new Map(),
    };
    function addRouter(node, currentPath, merger) {
        if (currentPath.length === 0) {
            if (node.merger) {
                throw new Error('duplicate route');
            }
            node.merger = merger;
        }
        else {
            const [key, ...remainingPath] = currentPath;
            let subNode = node.keyMap.get(key);
            if (subNode === undefined) {
                subNode = {
                    keyMap: new Map(),
                };
                node.keyMap.set(key, subNode);
            }
            addRouter(subNode, remainingPath, merger);
        }
    }
    for (const [path, merger] of routes) {
        addRouter(root, path, merger);
    }
    return (orig, left, right, path, mergeFn) => {
        let node = root;
        for (let i = 0; i < path.length; i++) {
            const childNode = node.keyMap.get(path[i]) || node.keyMap.get(RouteWildCard);
            if (childNode === undefined) {
                return CannotMerge;
            }
            node = childNode;
        }
        if (node.merger) {
            return node.merger(orig, left, right, path, mergeFn);
        }
        return CannotMerge;
    };
}

/**
 * This library modifies the diff-patch-match library by Neil Fraser
 * by removing the patch and match functionality and certain advanced
 * options in the diff function. The original license is as follows:
 *
 * ===
 *
 * Diff Match and Patch
 *
 * Copyright 2006 Google Inc.
 * http://code.google.com/p/google-diff-match-patch/
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * The data structure representing a diff is an array of tuples:
 * [[DIFF_DELETE, 'Hello'], [DIFF_INSERT, 'Goodbye'], [DIFF_EQUAL, ' world.']]
 * which means: delete 'Hello', add 'Goodbye' and keep ' world.'
 */
var DIFF_DELETE = -1;
var DIFF_INSERT = 1;
var DIFF_EQUAL = 0;


/**
 * Find the differences between two texts.  Simplifies the problem by stripping
 * any common prefix or suffix off the texts before diffing.
 * @param {string} text1 Old string to be diffed.
 * @param {string} text2 New string to be diffed.
 * @param {Int|Object} [cursor_pos] Edit position in text1 or object with more info
 * @return {Array} Array of diff tuples.
 */
function diff_main(text1, text2, cursor_pos, _fix_unicode) {
  // Check for equality
  if (text1 === text2) {
    if (text1) {
      return [[DIFF_EQUAL, text1]];
    }
    return [];
  }

  if (cursor_pos != null) {
    var editdiff = find_cursor_edit_diff(text1, text2, cursor_pos);
    if (editdiff) {
      return editdiff;
    }
  }

  // Trim off common prefix (speedup).
  var commonlength = diff_commonPrefix(text1, text2);
  var commonprefix = text1.substring(0, commonlength);
  text1 = text1.substring(commonlength);
  text2 = text2.substring(commonlength);

  // Trim off common suffix (speedup).
  commonlength = diff_commonSuffix(text1, text2);
  var commonsuffix = text1.substring(text1.length - commonlength);
  text1 = text1.substring(0, text1.length - commonlength);
  text2 = text2.substring(0, text2.length - commonlength);

  // Compute the diff on the middle block.
  var diffs = diff_compute_(text1, text2);

  // Restore the prefix and suffix.
  if (commonprefix) {
    diffs.unshift([DIFF_EQUAL, commonprefix]);
  }
  if (commonsuffix) {
    diffs.push([DIFF_EQUAL, commonsuffix]);
  }
  diff_cleanupMerge(diffs, _fix_unicode);
  return diffs;
}

/**
 * Find the differences between two texts.  Assumes that the texts do not
 * have any common prefix or suffix.
 * @param {string} text1 Old string to be diffed.
 * @param {string} text2 New string to be diffed.
 * @return {Array} Array of diff tuples.
 */
function diff_compute_(text1, text2) {
  var diffs;

  if (!text1) {
    // Just add some text (speedup).
    return [[DIFF_INSERT, text2]];
  }

  if (!text2) {
    // Just delete some text (speedup).
    return [[DIFF_DELETE, text1]];
  }

  var longtext = text1.length > text2.length ? text1 : text2;
  var shorttext = text1.length > text2.length ? text2 : text1;
  var i = longtext.indexOf(shorttext);
  if (i !== -1) {
    // Shorter text is inside the longer text (speedup).
    diffs = [
      [DIFF_INSERT, longtext.substring(0, i)],
      [DIFF_EQUAL, shorttext],
      [DIFF_INSERT, longtext.substring(i + shorttext.length)]
    ];
    // Swap insertions for deletions if diff is reversed.
    if (text1.length > text2.length) {
      diffs[0][0] = diffs[2][0] = DIFF_DELETE;
    }
    return diffs;
  }

  if (shorttext.length === 1) {
    // Single character string.
    // After the previous speedup, the character can't be an equality.
    return [[DIFF_DELETE, text1], [DIFF_INSERT, text2]];
  }

  // Check to see if the problem can be split in two.
  var hm = diff_halfMatch_(text1, text2);
  if (hm) {
    // A half-match was found, sort out the return data.
    var text1_a = hm[0];
    var text1_b = hm[1];
    var text2_a = hm[2];
    var text2_b = hm[3];
    var mid_common = hm[4];
    // Send both pairs off for separate processing.
    var diffs_a = diff_main(text1_a, text2_a);
    var diffs_b = diff_main(text1_b, text2_b);
    // Merge the results.
    return diffs_a.concat([[DIFF_EQUAL, mid_common]], diffs_b);
  }

  return diff_bisect_(text1, text2);
}

/**
 * Find the 'middle snake' of a diff, split the problem in two
 * and return the recursively constructed diff.
 * See Myers 1986 paper: An O(ND) Difference Algorithm and Its Variations.
 * @param {string} text1 Old string to be diffed.
 * @param {string} text2 New string to be diffed.
 * @return {Array} Array of diff tuples.
 * @private
 */
function diff_bisect_(text1, text2) {
  // Cache the text lengths to prevent multiple calls.
  var text1_length = text1.length;
  var text2_length = text2.length;
  var max_d = Math.ceil((text1_length + text2_length) / 2);
  var v_offset = max_d;
  var v_length = 2 * max_d;
  var v1 = new Array(v_length);
  var v2 = new Array(v_length);
  // Setting all elements to -1 is faster in Chrome & Firefox than mixing
  // integers and undefined.
  for (var x = 0; x < v_length; x++) {
    v1[x] = -1;
    v2[x] = -1;
  }
  v1[v_offset + 1] = 0;
  v2[v_offset + 1] = 0;
  var delta = text1_length - text2_length;
  // If the total number of characters is odd, then the front path will collide
  // with the reverse path.
  var front = (delta % 2 !== 0);
  // Offsets for start and end of k loop.
  // Prevents mapping of space beyond the grid.
  var k1start = 0;
  var k1end = 0;
  var k2start = 0;
  var k2end = 0;
  for (var d = 0; d < max_d; d++) {
    // Walk the front path one step.
    for (var k1 = -d + k1start; k1 <= d - k1end; k1 += 2) {
      var k1_offset = v_offset + k1;
      var x1;
      if (k1 === -d || (k1 !== d && v1[k1_offset - 1] < v1[k1_offset + 1])) {
        x1 = v1[k1_offset + 1];
      } else {
        x1 = v1[k1_offset - 1] + 1;
      }
      var y1 = x1 - k1;
      while (
        x1 < text1_length && y1 < text2_length &&
        text1.charAt(x1) === text2.charAt(y1)
      ) {
        x1++;
        y1++;
      }
      v1[k1_offset] = x1;
      if (x1 > text1_length) {
        // Ran off the right of the graph.
        k1end += 2;
      } else if (y1 > text2_length) {
        // Ran off the bottom of the graph.
        k1start += 2;
      } else if (front) {
        var k2_offset = v_offset + delta - k1;
        if (k2_offset >= 0 && k2_offset < v_length && v2[k2_offset] !== -1) {
          // Mirror x2 onto top-left coordinate system.
          var x2 = text1_length - v2[k2_offset];
          if (x1 >= x2) {
            // Overlap detected.
            return diff_bisectSplit_(text1, text2, x1, y1);
          }
        }
      }
    }

    // Walk the reverse path one step.
    for (var k2 = -d + k2start; k2 <= d - k2end; k2 += 2) {
      var k2_offset = v_offset + k2;
      var x2;
      if (k2 === -d || (k2 !== d && v2[k2_offset - 1] < v2[k2_offset + 1])) {
        x2 = v2[k2_offset + 1];
      } else {
        x2 = v2[k2_offset - 1] + 1;
      }
      var y2 = x2 - k2;
      while (
        x2 < text1_length && y2 < text2_length &&
        text1.charAt(text1_length - x2 - 1) === text2.charAt(text2_length - y2 - 1)
      ) {
        x2++;
        y2++;
      }
      v2[k2_offset] = x2;
      if (x2 > text1_length) {
        // Ran off the left of the graph.
        k2end += 2;
      } else if (y2 > text2_length) {
        // Ran off the top of the graph.
        k2start += 2;
      } else if (!front) {
        var k1_offset = v_offset + delta - k2;
        if (k1_offset >= 0 && k1_offset < v_length && v1[k1_offset] !== -1) {
          var x1 = v1[k1_offset];
          var y1 = v_offset + x1 - k1_offset;
          // Mirror x2 onto top-left coordinate system.
          x2 = text1_length - x2;
          if (x1 >= x2) {
            // Overlap detected.
            return diff_bisectSplit_(text1, text2, x1, y1);
          }
        }
      }
    }
  }
  // Diff took too long and hit the deadline or
  // number of diffs equals number of characters, no commonality at all.
  return [[DIFF_DELETE, text1], [DIFF_INSERT, text2]];
}

/**
 * Given the location of the 'middle snake', split the diff in two parts
 * and recurse.
 * @param {string} text1 Old string to be diffed.
 * @param {string} text2 New string to be diffed.
 * @param {number} x Index of split point in text1.
 * @param {number} y Index of split point in text2.
 * @return {Array} Array of diff tuples.
 */
function diff_bisectSplit_(text1, text2, x, y) {
  var text1a = text1.substring(0, x);
  var text2a = text2.substring(0, y);
  var text1b = text1.substring(x);
  var text2b = text2.substring(y);

  // Compute both diffs serially.
  var diffs = diff_main(text1a, text2a);
  var diffsb = diff_main(text1b, text2b);

  return diffs.concat(diffsb);
}

/**
 * Determine the common prefix of two strings.
 * @param {string} text1 First string.
 * @param {string} text2 Second string.
 * @return {number} The number of characters common to the start of each
 *     string.
 */
function diff_commonPrefix(text1, text2) {
  // Quick check for common null cases.
  if (!text1 || !text2 || text1.charAt(0) !== text2.charAt(0)) {
    return 0;
  }
  // Binary search.
  // Performance analysis: http://neil.fraser.name/news/2007/10/09/
  var pointermin = 0;
  var pointermax = Math.min(text1.length, text2.length);
  var pointermid = pointermax;
  var pointerstart = 0;
  while (pointermin < pointermid) {
    if (
      text1.substring(pointerstart, pointermid) ==
      text2.substring(pointerstart, pointermid)
    ) {
      pointermin = pointermid;
      pointerstart = pointermin;
    } else {
      pointermax = pointermid;
    }
    pointermid = Math.floor((pointermax - pointermin) / 2 + pointermin);
  }

  if (is_surrogate_pair_start(text1.charCodeAt(pointermid - 1))) {
    pointermid--;
  }

  return pointermid;
}

/**
 * Determine the common suffix of two strings.
 * @param {string} text1 First string.
 * @param {string} text2 Second string.
 * @return {number} The number of characters common to the end of each string.
 */
function diff_commonSuffix(text1, text2) {
  // Quick check for common null cases.
  if (!text1 || !text2 || text1.slice(-1) !== text2.slice(-1)) {
    return 0;
  }
  // Binary search.
  // Performance analysis: http://neil.fraser.name/news/2007/10/09/
  var pointermin = 0;
  var pointermax = Math.min(text1.length, text2.length);
  var pointermid = pointermax;
  var pointerend = 0;
  while (pointermin < pointermid) {
    if (
      text1.substring(text1.length - pointermid, text1.length - pointerend) ==
      text2.substring(text2.length - pointermid, text2.length - pointerend)
    ) {
      pointermin = pointermid;
      pointerend = pointermin;
    } else {
      pointermax = pointermid;
    }
    pointermid = Math.floor((pointermax - pointermin) / 2 + pointermin);
  }

  if (is_surrogate_pair_end(text1.charCodeAt(text1.length - pointermid))) {
    pointermid--;
  }

  return pointermid;
}

/**
 * Do the two texts share a substring which is at least half the length of the
 * longer text?
 * This speedup can produce non-minimal diffs.
 * @param {string} text1 First string.
 * @param {string} text2 Second string.
 * @return {Array.<string>} Five element Array, containing the prefix of
 *     text1, the suffix of text1, the prefix of text2, the suffix of
 *     text2 and the common middle.  Or null if there was no match.
 */
function diff_halfMatch_(text1, text2) {
  var longtext = text1.length > text2.length ? text1 : text2;
  var shorttext = text1.length > text2.length ? text2 : text1;
  if (longtext.length < 4 || shorttext.length * 2 < longtext.length) {
    return null;  // Pointless.
  }

  /**
   * Does a substring of shorttext exist within longtext such that the substring
   * is at least half the length of longtext?
   * Closure, but does not reference any external variables.
   * @param {string} longtext Longer string.
   * @param {string} shorttext Shorter string.
   * @param {number} i Start index of quarter length substring within longtext.
   * @return {Array.<string>} Five element Array, containing the prefix of
   *     longtext, the suffix of longtext, the prefix of shorttext, the suffix
   *     of shorttext and the common middle.  Or null if there was no match.
   * @private
   */
  function diff_halfMatchI_(longtext, shorttext, i) {
    // Start with a 1/4 length substring at position i as a seed.
    var seed = longtext.substring(i, i + Math.floor(longtext.length / 4));
    var j = -1;
    var best_common = '';
    var best_longtext_a, best_longtext_b, best_shorttext_a, best_shorttext_b;
    while ((j = shorttext.indexOf(seed, j + 1)) !== -1) {
      var prefixLength = diff_commonPrefix(
        longtext.substring(i), shorttext.substring(j));
      var suffixLength = diff_commonSuffix(
        longtext.substring(0, i), shorttext.substring(0, j));
      if (best_common.length < suffixLength + prefixLength) {
        best_common = shorttext.substring(
          j - suffixLength, j) + shorttext.substring(j, j + prefixLength);
        best_longtext_a = longtext.substring(0, i - suffixLength);
        best_longtext_b = longtext.substring(i + prefixLength);
        best_shorttext_a = shorttext.substring(0, j - suffixLength);
        best_shorttext_b = shorttext.substring(j + prefixLength);
      }
    }
    if (best_common.length * 2 >= longtext.length) {
      return [
        best_longtext_a, best_longtext_b,
        best_shorttext_a, best_shorttext_b, best_common
      ];
    } else {
      return null;
    }
  }

  // First check if the second quarter is the seed for a half-match.
  var hm1 = diff_halfMatchI_(longtext, shorttext, Math.ceil(longtext.length / 4));
  // Check again based on the third quarter.
  var hm2 = diff_halfMatchI_(longtext, shorttext, Math.ceil(longtext.length / 2));
  var hm;
  if (!hm1 && !hm2) {
    return null;
  } else if (!hm2) {
    hm = hm1;
  } else if (!hm1) {
    hm = hm2;
  } else {
    // Both matched.  Select the longest.
    hm = hm1[4].length > hm2[4].length ? hm1 : hm2;
  }

  // A half-match was found, sort out the return data.
  var text1_a, text1_b, text2_a, text2_b;
  if (text1.length > text2.length) {
    text1_a = hm[0];
    text1_b = hm[1];
    text2_a = hm[2];
    text2_b = hm[3];
  } else {
    text2_a = hm[0];
    text2_b = hm[1];
    text1_a = hm[2];
    text1_b = hm[3];
  }
  var mid_common = hm[4];
  return [text1_a, text1_b, text2_a, text2_b, mid_common];
}

/**
 * Reorder and merge like edit sections.  Merge equalities.
 * Any edit section can move as long as it doesn't cross an equality.
 * @param {Array} diffs Array of diff tuples.
 * @param {boolean} fix_unicode Whether to normalize to a unicode-correct diff
 */
function diff_cleanupMerge(diffs, fix_unicode) {
  diffs.push([DIFF_EQUAL, '']);  // Add a dummy entry at the end.
  var pointer = 0;
  var count_delete = 0;
  var count_insert = 0;
  var text_delete = '';
  var text_insert = '';
  var commonlength;
  while (pointer < diffs.length) {
    if (pointer < diffs.length - 1 && !diffs[pointer][1]) {
      diffs.splice(pointer, 1);
      continue;
    }
    switch (diffs[pointer][0]) {
      case DIFF_INSERT:

        count_insert++;
        text_insert += diffs[pointer][1];
        pointer++;
        break;
      case DIFF_DELETE:
        count_delete++;
        text_delete += diffs[pointer][1];
        pointer++;
        break;
      case DIFF_EQUAL:
        var previous_equality = pointer - count_insert - count_delete - 1;
        if (fix_unicode) {
          // prevent splitting of unicode surrogate pairs.  when fix_unicode is true,
          // we assume that the old and new text in the diff are complete and correct
          // unicode-encoded JS strings, but the tuple boundaries may fall between
          // surrogate pairs.  we fix this by shaving off stray surrogates from the end
          // of the previous equality and the beginning of this equality.  this may create
          // empty equalities or a common prefix or suffix.  for example, if AB and AC are
          // emojis, `[[0, 'A'], [-1, 'BA'], [0, 'C']]` would turn into deleting 'ABAC' and
          // inserting 'AC', and then the common suffix 'AC' will be eliminated.  in this
          // particular case, both equalities go away, we absorb any previous inequalities,
          // and we keep scanning for the next equality before rewriting the tuples.
          if (previous_equality >= 0 && ends_with_pair_start(diffs[previous_equality][1])) {
            var stray = diffs[previous_equality][1].slice(-1);
            diffs[previous_equality][1] = diffs[previous_equality][1].slice(0, -1);
            text_delete = stray + text_delete;
            text_insert = stray + text_insert;
            if (!diffs[previous_equality][1]) {
              // emptied out previous equality, so delete it and include previous delete/insert
              diffs.splice(previous_equality, 1);
              pointer--;
              var k = previous_equality - 1;
              if (diffs[k] && diffs[k][0] === DIFF_INSERT) {
                count_insert++;
                text_insert = diffs[k][1] + text_insert;
                k--;
              }
              if (diffs[k] && diffs[k][0] === DIFF_DELETE) {
                count_delete++;
                text_delete = diffs[k][1] + text_delete;
                k--;
              }
              previous_equality = k;
            }
          }
          if (starts_with_pair_end(diffs[pointer][1])) {
            var stray = diffs[pointer][1].charAt(0);
            diffs[pointer][1] = diffs[pointer][1].slice(1);
            text_delete += stray;
            text_insert += stray;
          }
        }
        if (pointer < diffs.length - 1 && !diffs[pointer][1]) {
          // for empty equality not at end, wait for next equality
          diffs.splice(pointer, 1);
          break;
        }
        if (text_delete.length > 0 || text_insert.length > 0) {
          // note that diff_commonPrefix and diff_commonSuffix are unicode-aware
          if (text_delete.length > 0 && text_insert.length > 0) {
            // Factor out any common prefixes.
            commonlength = diff_commonPrefix(text_insert, text_delete);
            if (commonlength !== 0) {
              if (previous_equality >= 0) {
                diffs[previous_equality][1] += text_insert.substring(0, commonlength);
              } else {
                diffs.splice(0, 0, [DIFF_EQUAL, text_insert.substring(0, commonlength)]);
                pointer++;
              }
              text_insert = text_insert.substring(commonlength);
              text_delete = text_delete.substring(commonlength);
            }
            // Factor out any common suffixes.
            commonlength = diff_commonSuffix(text_insert, text_delete);
            if (commonlength !== 0) {
              diffs[pointer][1] =
                text_insert.substring(text_insert.length - commonlength) + diffs[pointer][1];
              text_insert = text_insert.substring(0, text_insert.length - commonlength);
              text_delete = text_delete.substring(0, text_delete.length - commonlength);
            }
          }
          // Delete the offending records and add the merged ones.
          var n = count_insert + count_delete;
          if (text_delete.length === 0 && text_insert.length === 0) {
            diffs.splice(pointer - n, n);
            pointer = pointer - n;
          } else if (text_delete.length === 0) {
            diffs.splice(pointer - n, n, [DIFF_INSERT, text_insert]);
            pointer = pointer - n + 1;
          } else if (text_insert.length === 0) {
            diffs.splice(pointer - n, n, [DIFF_DELETE, text_delete]);
            pointer = pointer - n + 1;
          } else {
            diffs.splice(pointer - n, n, [DIFF_DELETE, text_delete], [DIFF_INSERT, text_insert]);
            pointer = pointer - n + 2;
          }
        }
        if (pointer !== 0 && diffs[pointer - 1][0] === DIFF_EQUAL) {
          // Merge this equality with the previous one.
          diffs[pointer - 1][1] += diffs[pointer][1];
          diffs.splice(pointer, 1);
        } else {
          pointer++;
        }
        count_insert = 0;
        count_delete = 0;
        text_delete = '';
        text_insert = '';
        break;
    }
  }
  if (diffs[diffs.length - 1][1] === '') {
    diffs.pop();  // Remove the dummy entry at the end.
  }

  // Second pass: look for single edits surrounded on both sides by equalities
  // which can be shifted sideways to eliminate an equality.
  // e.g: A<ins>BA</ins>C -> <ins>AB</ins>AC
  var changes = false;
  pointer = 1;
  // Intentionally ignore the first and last element (don't need checking).
  while (pointer < diffs.length - 1) {
    if (diffs[pointer - 1][0] === DIFF_EQUAL &&
      diffs[pointer + 1][0] === DIFF_EQUAL) {
      // This is a single edit surrounded by equalities.
      if (diffs[pointer][1].substring(diffs[pointer][1].length -
        diffs[pointer - 1][1].length) === diffs[pointer - 1][1]) {
        // Shift the edit over the previous equality.
        diffs[pointer][1] = diffs[pointer - 1][1] +
          diffs[pointer][1].substring(0, diffs[pointer][1].length -
            diffs[pointer - 1][1].length);
        diffs[pointer + 1][1] = diffs[pointer - 1][1] + diffs[pointer + 1][1];
        diffs.splice(pointer - 1, 1);
        changes = true;
      } else if (diffs[pointer][1].substring(0, diffs[pointer + 1][1].length) ==
        diffs[pointer + 1][1]) {
        // Shift the edit over the next equality.
        diffs[pointer - 1][1] += diffs[pointer + 1][1];
        diffs[pointer][1] =
          diffs[pointer][1].substring(diffs[pointer + 1][1].length) +
          diffs[pointer + 1][1];
        diffs.splice(pointer + 1, 1);
        changes = true;
      }
    }
    pointer++;
  }
  // If shifts were made, the diff needs reordering and another shift sweep.
  if (changes) {
    diff_cleanupMerge(diffs, fix_unicode);
  }
}
function is_surrogate_pair_start(charCode) {
  return charCode >= 0xD800 && charCode <= 0xDBFF;
}

function is_surrogate_pair_end(charCode) {
  return charCode >= 0xDC00 && charCode <= 0xDFFF;
}

function starts_with_pair_end(str) {
  return is_surrogate_pair_end(str.charCodeAt(0));
}

function ends_with_pair_start(str) {
  return is_surrogate_pair_start(str.charCodeAt(str.length - 1));
}

function remove_empty_tuples(tuples) {
  var ret = [];
  for (var i = 0; i < tuples.length; i++) {
    if (tuples[i][1].length > 0) {
      ret.push(tuples[i]);
    }
  }
  return ret;
}

function make_edit_splice(before, oldMiddle, newMiddle, after) {
  if (ends_with_pair_start(before) || starts_with_pair_end(after)) {
    return null;
  }
  return remove_empty_tuples([
    [DIFF_EQUAL, before],
    [DIFF_DELETE, oldMiddle],
    [DIFF_INSERT, newMiddle],
    [DIFF_EQUAL, after]
  ]);
}

function find_cursor_edit_diff(oldText, newText, cursor_pos) {
  // note: this runs after equality check has ruled out exact equality
  var oldRange = typeof cursor_pos === 'number' ?
    { index: cursor_pos, length: 0 } : cursor_pos.oldRange;
  var newRange = typeof cursor_pos === 'number' ?
    null : cursor_pos.newRange;
  // take into account the old and new selection to generate the best diff
  // possible for a text edit.  for example, a text change from "xxx" to "xx"
  // could be a delete or forwards-delete of any one of the x's, or the
  // result of selecting two of the x's and typing "x".
  var oldLength = oldText.length;
  var newLength = newText.length;
  if (oldRange.length === 0 && (newRange === null || newRange.length === 0)) {
    // see if we have an insert or delete before or after cursor
    var oldCursor = oldRange.index;
    var oldBefore = oldText.slice(0, oldCursor);
    var oldAfter = oldText.slice(oldCursor);
    var maybeNewCursor = newRange ? newRange.index : null;
    editBefore: {
      // is this an insert or delete right before oldCursor?
      var newCursor = oldCursor + newLength - oldLength;
      if (maybeNewCursor !== null && maybeNewCursor !== newCursor) {
        break editBefore;
      }
      if (newCursor < 0 || newCursor > newLength) {
        break editBefore;
      }
      var newBefore = newText.slice(0, newCursor);
      var newAfter = newText.slice(newCursor);
      if (newAfter !== oldAfter) {
        break editBefore;
      }
      var prefixLength = Math.min(oldCursor, newCursor);
      var oldPrefix = oldBefore.slice(0, prefixLength);
      var newPrefix = newBefore.slice(0, prefixLength);
      if (oldPrefix !== newPrefix) {
        break editBefore;
      }
      var oldMiddle = oldBefore.slice(prefixLength);
      var newMiddle = newBefore.slice(prefixLength);
      return make_edit_splice(oldPrefix, oldMiddle, newMiddle, oldAfter);
    }
    editAfter: {
      // is this an insert or delete right after oldCursor?
      if (maybeNewCursor !== null && maybeNewCursor !== oldCursor) {
        break editAfter;
      }
      var cursor = oldCursor;
      var newBefore = newText.slice(0, cursor);
      var newAfter = newText.slice(cursor);
      if (newBefore !== oldBefore) {
        break editAfter;
      }
      var suffixLength = Math.min(oldLength - cursor, newLength - cursor);
      var oldSuffix = oldAfter.slice(oldAfter.length - suffixLength);
      var newSuffix = newAfter.slice(newAfter.length - suffixLength);
      if (oldSuffix !== newSuffix) {
        break editAfter;
      }
      var oldMiddle = oldAfter.slice(0, oldAfter.length - suffixLength);
      var newMiddle = newAfter.slice(0, newAfter.length - suffixLength);
      return make_edit_splice(oldBefore, oldMiddle, newMiddle, oldSuffix);
    }
  }
  if (oldRange.length > 0 && newRange && newRange.length === 0) {
    replaceRange: {
      // see if diff could be a splice of the old selection range
      var oldPrefix = oldText.slice(0, oldRange.index);
      var oldSuffix = oldText.slice(oldRange.index + oldRange.length);
      var prefixLength = oldPrefix.length;
      var suffixLength = oldSuffix.length;
      if (newLength < prefixLength + suffixLength) {
        break replaceRange;
      }
      var newPrefix = newText.slice(0, prefixLength);
      var newSuffix = newText.slice(newLength - suffixLength);
      if (oldPrefix !== newPrefix || oldSuffix !== newSuffix) {
        break replaceRange;
      }
      var oldMiddle = oldText.slice(prefixLength, oldLength - suffixLength);
      var newMiddle = newText.slice(prefixLength, newLength - suffixLength);
      return make_edit_splice(oldPrefix, oldMiddle, newMiddle, oldSuffix);
    }
  }

  return null;
}

function diff$1(text1, text2, cursor_pos) {
  // only pass fix_unicode=true at the top level, not when diff_main is
  // recursively invoked
  return diff_main(text1, text2, cursor_pos, true);
}

diff$1.INSERT = DIFF_INSERT;
diff$1.DELETE = DIFF_DELETE;
diff$1.EQUAL = DIFF_EQUAL;

var diff_1 = diff$1;

var bin = {};

Object.defineProperty(bin,"__esModule",{value:true});bin.applyPatch=bin.calcPatch=bin.lcs=bin.diff=diff_core_1 = bin.diff_core=void 0;function diff_internal(state,c){let{i,N,j,M,Z,b,eq,stack_top,stack_base,}=state;for(;;){switch(c){case 0:{Z_block:while(N>0&&M>0){b.fill(0,0,2*Z);const W=N-M;const L=N+M;const parity=L&1;const offsetx=i+N-1;const offsety=j+M-1;const hmax=(L+parity)/2;h_loop:for(let h=0;h<=hmax;h++){const kmin=2*Math.max(0,h-M)-h;const kmax=h-2*Math.max(0,h-N);for(let k=kmin;k<=kmax;k+=2){const Zk=(k%Z)+Z;const gkm=b[(Zk-1)%Z];const gkp=b[(Zk+1)%Z];const u=(k===-h||(k!==h&&gkm<gkp))?gkp:gkm+1;const v=u-k;let x=u;let y=v;let z;while(x<N&&y<M&&eq(i+x,j+y))
x++,y++;b[Zk%Z]=x;if(parity===1&&(z=W-k)>=1-h&&z<h&&x+b[Z+(Z+z%Z)%Z]>=N){if(h>1||x!==u){stack_base[stack_top++]=i+x;stack_base[stack_top++]=N-x;stack_base[stack_top++]=j+y;stack_base[stack_top++]=M-y;N=u;M=v;Z=2*(Math.min(N,M)+1);continue Z_block;}
else
break h_loop;}}
for(let k=kmin;k<=kmax;k+=2){const Zk=(k%Z)+Z;const pkm=b[Z+(Zk-1)%Z];const pkp=b[Z+(Zk+1)%Z];const u=(k===-h||(k!==h&&pkm<pkp))?pkp:pkm+1;const v=u-k;let x=u;let y=v;let z;while(x<N&&y<M&&eq(offsetx-x,offsety-y))
x++,y++;b[Z+Zk%Z]=x;if(parity===0&&(z=W-k)>=-h&&z<=h&&x+b[(Z+z%Z)%Z]>=N){if(h>0||x!==u){stack_base[stack_top++]=i+N-u;stack_base[stack_top++]=u;stack_base[stack_top++]=j+M-v;stack_base[stack_top++]=v;N=N-x;M=M-y;Z=2*(Math.min(N,M)+1);continue Z_block;}
else
break h_loop;}}}
if(N===M)
continue;if(M>N){i+=N;j+=N;M-=N;N=0;}
else {i+=M;j+=M;N-=M;M=0;}
break;}
if(N+M!==0){if(state.pxe===i||state.pye===j){state.pxe=i+N;state.pye=j+M;}
else {const sx=state.pxs;state.oxs=state.pxs;state.oxe=state.pxe;state.oys=state.pys;state.oye=state.pye;state.pxs=i;state.pxe=i+N;state.pys=j;state.pye=j+M;if(sx>=0){state.i=i;state.N=N;state.j=j;state.M=M;state.Z=Z;state.stack_top=stack_top;return 1;}}}}
case 1:{if(stack_top===0)
return 2;M=stack_base[--stack_top];j=stack_base[--stack_top];N=stack_base[--stack_top];i=stack_base[--stack_top];Z=2*(Math.min(N,M)+1);c=0;}}}}
function*diff_core(i,N,j,M,eq){const Z=(Math.min(N,M)+1)*2;const L=N+M;const b=new(L<256?Uint8Array:L<65536?Uint16Array:Uint32Array)(2*Z);const state={i,N,j,M,Z,b,eq,pxs:-1,pxe:-1,pys:-1,pye:-1,oxs:-1,oxe:-1,oys:-1,oye:-1,stack_top:0,stack_base:[],};let c=diff_internal(state,0);while(c===1){yield [state.oxs,state.oxe,state.oys,state.oye];c=diff_internal(state,c);}
if(state.pxs>=0)
yield [state.pxs,state.pxe,state.pys,state.pye];}
var diff_core_1 = bin.diff_core=diff_core;function*diff(xs,ys){let[i,N,M]=[0,xs.length,ys.length];while(i<N&&i<M&&xs[i]===ys[i])
i++;if(i===N&&i===M)
return;while(xs[--N]===ys[--M]&&N>i&&M>i);const eq=(x,y)=>xs[x]===ys[y];yield*diff_core(i,N+1-i,i,M+1-i,eq);}
bin.diff=diff;function*lcs(xs,ys){const N=xs.length;let[i,j,sx,ex,ey]=[0,0,0,0,0];for(const rec of diff(xs,ys)){[sx,ex,,ey]=rec;if(i!==sx){rec.length--;[rec[0],rec[1],rec[2]]=[i,j,sx-i];yield rec;}
[i,j]=[ex,ey];}
if(i<N)
yield [i,j,N-i];}
bin.lcs=lcs;function*calcPatch(xs,ys){const slice=ArrayBuffer.isView(xs)?Uint8Array.prototype.subarray:xs.slice;for(const[dels,dele,inss,inse]of diff(xs,ys)){yield [dels,dele,slice.call(ys,inss,inse)];}}
bin.calcPatch=calcPatch;function*applyPatch(xs,patch){let i=0;const slice=ArrayBuffer.isView(xs)?Uint8Array.prototype.subarray:xs.slice;for(const[dels,dele,ins]of patch){if(i<dels)
yield slice.call(xs,i,dels);if(ins.length>0)
yield ins;i=dele;}
if(i<xs.length)
yield slice.call(xs,i);}
bin.applyPatch=applyPatch;

// Based on https://github.com/bhousel/node-diff3
function makeRange(location, length) {
    return { location, length };
}
function upperBound({ location, length }) {
    return location + length;
}
function diffIndicesString(a, b) {
    const diffResult = diff_1(a, b);
    let aIndex = 0;
    let bIndex = 0;
    let lastA = 0;
    let lastB = 0;
    const result = [];
    function flush() {
        if (aIndex > lastA || bIndex > lastB) {
            result.push({
                a: makeRange(lastA, aIndex - lastA),
                b: makeRange(lastB, bIndex - lastB),
            });
        }
    }
    for (const [type, str] of diffResult) {
        switch (type) {
            case diff_1.EQUAL: {
                flush();
                aIndex += str.length;
                bIndex += str.length;
                lastA = aIndex;
                lastB = bIndex;
                break;
            }
            case diff_1.INSERT: {
                bIndex += str.length;
                break;
            }
            case diff_1.DELETE: {
                aIndex += str.length;
                break;
            }
        }
    }
    flush();
    return result;
}
function areReferenceEqual(a, b) {
    return a === b;
}
function diffIndicesArray(a, b, areEqual = areReferenceEqual) {
    if (a === b) {
        return [];
    }
    const result = [];
    // following the pattern of fastMyersDiff.diff, compute common prefix, suffix, and equality
    // to reduce the amount of work needed by fastMyersDiff.diff_core
    // eliminate common prefix
    let offset = 0;
    let aLength = a.length;
    let bLength = b.length;
    while (offset < aLength &&
        offset < bLength &&
        areEqual(a[offset], b[offset])) {
        offset++;
    }
    if (offset === aLength && offset === bLength) {
        return [];
    }
    // eliminate common suffix
    while (aLength > offset && bLength > offset) {
        if (!areEqual(a[aLength - 1], b[bLength - 1])) {
            break;
        }
        aLength--;
        bLength--;
    }
    for (const [aStart, aEnd, bStart, bEnd] of diff_core_1(offset, aLength - offset, offset, bLength - offset, (i, j) => areEqual(a[i], b[j]))) {
        result.push({
            a: makeRange(aStart, aEnd - aStart),
            b: makeRange(bStart, bEnd - bStart),
        });
    }
    return result;
}
function diffIndices(a, b) {
    if (typeof a === 'string' && typeof b === 'string') {
        return diffIndicesString(a, b);
    }
    return diffIndicesArray(a, b);
}
// Given three files, A, O, and B, where both A and B are
// independently derived from O, returns a fairly complicated
// internal representation of merge decisions it's taken. The
// interested reader may wish to consult
//
// Sanjeev Khanna, Keshav Kunal, and Benjamin C. Pierce.
// 'A Formal Investigation of ' In Arvind and Prasad,
// editors, Foundations of Software Technology and Theoretical
// Computer Science (FSTTCS), December 2007.
//
// (http://www.cis.upenn.edu/~bcpierce/papers/diff3-short.pdf)
function diff3MergeIndices(o, a, b) {
    const m1 = diffIndices(o, a);
    const m2 = diffIndices(o, b);
    const hunks = [];
    function addHunk(h, side) {
        hunks.push({
            side,
            oRange: h.a,
            sideRange: h.b,
        });
    }
    for (let i = 0; i < m1.length; i++) {
        addHunk(m1[i], 'a');
    }
    for (let i = 0; i < m2.length; i++) {
        addHunk(m2[i], 'b');
    }
    hunks.sort((x, y) => x.oRange.location - y.oRange.location);
    const result = [];
    let oOffset = 0;
    let aOffset = 0;
    let bOffset = 0;
    function copyCommon(targetOffset) {
        const delta = targetOffset - oOffset;
        if (delta > 0) {
            result.push({
                type: 'okA',
                length: delta,
                aIndex: aOffset,
                oIndex: oOffset,
                bIndex: bOffset,
            });
            aOffset += delta;
            bOffset += delta;
            oOffset += delta;
        }
    }
    for (let hunkIndex = 0; hunkIndex < hunks.length; hunkIndex++) {
        const firstHunkIndex = hunkIndex;
        let hunk = hunks[hunkIndex];
        const regionLhs = hunk.oRange.location;
        let regionRhs = upperBound(hunk.oRange);
        while (hunkIndex < hunks.length - 1) {
            const maybeOverlapping = hunks[hunkIndex + 1];
            const maybeLhs = maybeOverlapping.oRange.location;
            if (maybeLhs > regionRhs) {
                break;
            }
            regionRhs = Math.max(regionRhs, upperBound(maybeOverlapping.oRange));
            hunkIndex++;
        }
        copyCommon(regionLhs);
        if (firstHunkIndex === hunkIndex) {
            // The 'overlap' was only one hunk long, meaning that
            // there's no conflict here. Either a and o were the
            // same, or b and o were the same.
            if (hunk.sideRange.length > 0) {
                if (hunk.side === 'a') {
                    result.push({
                        type: 'okA',
                        length: hunk.sideRange.length,
                        oIndex: undefined,
                        aIndex: hunk.sideRange.location,
                        bIndex: undefined,
                    });
                }
                else {
                    result.push({
                        type: 'okB',
                        length: hunk.sideRange.length,
                        oIndex: undefined,
                        aIndex: undefined,
                        bIndex: hunk.sideRange.location,
                    });
                }
            }
            const delta = regionRhs - oOffset;
            oOffset = regionRhs;
            aOffset =
                hunk.side === 'a' ? upperBound(hunk.sideRange) : aOffset + delta;
            bOffset =
                hunk.side === 'b' ? upperBound(hunk.sideRange) : bOffset + delta;
        }
        else {
            // A proper conflict. Determine the extents of the
            // regions involved from a, o and b. Effectively merge
            // all the hunks on the left into one giant hunk, and
            // do the same for the right; then, correct for skew
            // in the regions of o that each side changed, and
            // report appropriate spans for the three sides.
            const regions = {
                a: [a.length, -1, o.length, -1],
                b: [b.length, -1, o.length, -1],
            };
            for (let i = firstHunkIndex; i <= hunkIndex; i++) {
                hunk = hunks[i];
                const side = hunk.side;
                const r = regions[side];
                const oLhs = hunk.oRange.location;
                const oRhs = upperBound(hunk.oRange);
                const abLhs = hunk.sideRange.location;
                const abRhs = upperBound(hunk.sideRange);
                r[0] = Math.min(abLhs, r[0]);
                r[1] = Math.max(abRhs, r[1]);
                r[2] = Math.min(oLhs, r[2]);
                r[3] = Math.max(oRhs, r[3]);
            }
            const aLhs = regions.a[0] + (regionLhs - regions.a[2]);
            const aRhs = regions.a[1] + (regionRhs - regions.a[3]);
            const bLhs = regions.b[0] + (regionLhs - regions.b[2]);
            const bRhs = regions.b[1] + (regionRhs - regions.b[3]);
            result.push({
                type: 'conflict',
                aRange: makeRange(aLhs, aRhs - aLhs),
                oRange: makeRange(regionLhs, regionRhs - regionLhs),
                bRange: makeRange(bLhs, bRhs - bLhs),
            });
            oOffset = regionRhs;
            aOffset = aRhs;
            bOffset = bRhs;
        }
    }
    copyCommon(o.length);
    return result;
}

function diff3Keys(origKeys, leftKeys, rightKeys, callback, allowOrderConflicts = false) {
    const indices = diff3MergeIndices(origKeys, leftKeys, rightKeys);
    const seenKeys = new Set();
    function emit(key) {
        if (seenKeys.has(key)) {
            if (!allowOrderConflicts) {
                throw new Error('order conflict');
            }
        }
        else {
            seenKeys.add(key);
            callback(key);
        }
    }
    for (let i = 0; i < indices.length; i++) {
        const index = indices[i];
        if (index.type === 'conflict') {
            const leftStart = index.aRange.location;
            const leftEnd = upperBound(index.aRange);
            const origStart = index.oRange.location;
            const origEnd = upperBound(index.oRange);
            const rightStart = index.bRange.location;
            const rightEnd = upperBound(index.bRange);
            const rightSlice = new Set();
            for (let i = rightStart; i < rightEnd; i++) {
                rightSlice.add(rightKeys[i]);
            }
            const origSlice = new Set();
            for (let i = origStart; i < origEnd; i++) {
                origSlice.add(origKeys[i]);
            }
            for (let j = leftStart; j < leftEnd; j++) {
                const key = leftKeys[j];
                if (rightSlice.has(key) || !origSlice.has(key)) {
                    emit(key);
                }
            }
            for (let j = rightStart; j < rightEnd; j++) {
                const key = rightKeys[j];
                // Added by right
                if (!origSlice.has(key)) {
                    emit(key);
                }
            }
        }
        else {
            const start = index.type === 'okA' ? index.aIndex : index.bIndex;
            const arr = index.type === 'okA' ? leftKeys : rightKeys;
            const end = start + index.length;
            for (let j = start; j < end; j++) {
                emit(arr[j]);
            }
        }
    }
}

function internalTrimergeOrderedMap(origMap, leftMap, rightMap, path, mergeFn, allowOrderConflicts, callback) {
    const leftRightKeys = new Set(leftMap.keys());
    for (const key of rightMap.keys()) {
        leftRightKeys.add(key);
    }
    // Merge all values first (might restore some deleted values)
    const mergedValues = new Map();
    // Keys that have been removed from left or right but were restored by mergeFn
    const restoreLeft = new Set();
    const restoreRight = new Set();
    for (const key of leftRightKeys) {
        const left = leftMap.get(key);
        const right = rightMap.get(key);
        const merged = mergeFn(origMap.get(key), left, right, [...path, key], mergeFn);
        if (merged !== undefined) {
            if (!leftMap.has(key)) {
                restoreLeft.add(key);
            }
            else if (!rightMap.has(key)) {
                restoreRight.add(key);
            }
            mergedValues.set(key, merged);
        }
    }
    const orig = Array.from(origMap.keys());
    const left = restoreDeletedKeys(orig, leftMap, restoreLeft);
    const right = restoreDeletedKeys(orig, rightMap, restoreRight);
    // While merging, we want to detect if the merged result is identical to
    // either left or right, i.e. same key/value pairs in the same order
    let leftSame = true;
    let rightSame = true;
    // To track order, we need to iterate over the map
    const leftIterator = leftMap.entries();
    const rightIterator = rightMap.entries();
    diff3Keys(orig, left, right, (key) => {
        const merged = mergedValues.get(key);
        if (merged !== undefined) {
            if (leftSame) {
                // Check that next key and value match
                const left = leftIterator.next().value;
                if (!left || key !== left[0] || merged !== left[1]) {
                    leftSame = false;
                }
            }
            if (rightSame) {
                // Check that next key and value match
                const right = rightIterator.next().value;
                if (!right || key !== right[0] || merged !== right[1]) {
                    rightSame = false;
                }
            }
            callback(key, merged);
        }
    }, allowOrderConflicts);
    // If everything so far is the same, we need to also check there aren't still
    // items in left (e.g. left.size > result.size)
    if (leftSame && leftIterator.next().done) {
        return 'left';
    }
    // Ditto right
    if (rightSame && rightIterator.next().done) {
        return 'right';
    }
    return undefined;
}
/**
 * Used by internalTrimergeOrderedMap to add back keys from `a` that were
 * removed in `b`, but need to be added back in their original location.
 *
 * @param a base array of items
 * @param bMap map of items (either left or right, in trimerge terms)
 * @param keysToRestore set of items from `a` that were removed in `bMap` and need
 *  to be added back. this should NOT include anything from bMap
 */
function restoreDeletedKeys(a, bMap, keysToRestore) {
    const b = Array.from(bMap.keys());
    if (keysToRestore.size === 0) {
        return b;
    }
    // Otherwise we need build a new B that inserts the missing keys as close to
    // their original locations as possible
    const newB = [];
    let bEnd = 0;
    for (const diff of diffIndices(a, b)) {
        const bStart = diff.b.location;
        if (bEnd < bStart) {
            // Add beginning of B (no edits)
            newB.push(...b.slice(bEnd, bStart));
        }
        // Add keys from A that were deleted in B
        const aStart = diff.a.location;
        const aEnd = aStart + diff.a.length;
        for (let i = aStart; i < aEnd; i++) {
            const item = a[i];
            if (keysToRestore.has(item)) {
                // Key was in A, but deleted in B
                newB.push(item);
            }
        }
        // Add diffed keys from B
        bEnd = bStart + diff.b.length;
        newB.push(...b.slice(bStart, bEnd));
    }
    if (bEnd < b.length) {
        // Add end of B
        newB.push(...b.slice(bEnd));
    }
    return newB;
}

function* iterateKeys(...maps) {
    for (const map of maps) {
        yield* map.keys();
    }
}
function trimergeUnorderedMap(orig, left, right, path, merge) {
    if (!(orig instanceof Map) ||
        !(left instanceof Map) ||
        !(right instanceof Map)) {
        return CannotMerge;
    }
    const newMap = new Map();
    const keys = new Set(iterateKeys(orig, left, right));
    keys.forEach((key) => {
        const merged = merge(orig.get(key), left.get(key), right.get(key), [...path, key], merge);
        if (merged !== undefined) {
            newMap.set(key, merged);
        }
    });
    return newMap;
}
function trimergeMapCreator(allowOrderConflicts) {
    return function trimergeMap(orig, left, right, path, mergeFn) {
        if (!(orig instanceof Map) ||
            !(left instanceof Map) ||
            !(right instanceof Map)) {
            return CannotMerge;
        }
        const mergedMap = new Map();
        switch (internalTrimergeOrderedMap(orig, left, right, path, mergeFn, allowOrderConflicts, (key, merged) => mergedMap.set(key, merged))) {
            case 'left':
                return left;
            case 'right':
                return right;
            default:
                return mergedMap;
        }
    };
}
const trimergeMap = trimergeMapCreator(false);

function forEachSliceRanges(ranges, sliceStart, sliceEnd, offset, callback) {
    ranges.forEach(({ start, end, value }) => {
        if (start > sliceEnd || end < sliceStart) {
            return;
        }
        callback({
            start: offset + Math.max(0, start - sliceStart),
            end: offset + Math.min(sliceEnd - sliceStart, end - sliceStart),
            value,
        });
    });
}
function diff3MergeStringRanges(base, left, right, baseRanges = [], leftRanges = [], rightRanges = []) {
    let result = '';
    const ranges = [];
    const indices = diff3MergeIndices(base, left, right);
    const addRange = (range) => ranges.push(range);
    for (let i = 0; i < indices.length; i++) {
        const index = indices[i];
        if (index.type === 'conflict') {
            const { aRange: { location: leftStart, length: leftLength }, oRange: { location: baseStart, length: baseLength }, bRange: { location: rightStart, length: rightLength }, } = index;
            const leftEnd = upperBound(index.aRange);
            const baseEnd = upperBound(index.oRange);
            const rightEnd = upperBound(index.bRange);
            const leftSlice = left.slice(leftStart, leftEnd);
            const baseSlice = base.slice(baseStart, baseEnd);
            const rightSlice = right.slice(rightStart, rightEnd);
            forEachSliceRanges(leftRanges, leftStart, leftEnd, result.length, addRange);
            forEachSliceRanges(rightRanges, rightStart, rightEnd, result.length, addRange);
            if (baseLength) {
                // If left/right slice starts/ends with base slice,
                // then it was removed from the other slice, so don't include it
                if (leftSlice.startsWith(baseSlice)) {
                    result += leftSlice.slice(baseLength);
                    result += rightSlice;
                }
                else if (rightSlice.startsWith(baseSlice)) {
                    result += leftSlice;
                    result += rightSlice.slice(baseLength);
                }
                else if (leftSlice.endsWith(baseSlice)) {
                    result += leftSlice.slice(0, leftLength - baseLength);
                    result += rightSlice;
                }
                else if (rightSlice.endsWith(baseSlice)) {
                    result += leftSlice;
                    result += rightSlice.slice(0, rightLength - baseLength);
                }
                else {
                    result += leftSlice;
                    result += rightSlice;
                }
            }
            else {
                result += leftSlice;
                result += rightSlice;
            }
        }
        else {
            const start = index.type === 'okA' ? index.aIndex : index.bIndex;
            const end = start + index.length;
            const str = index.type === 'okA' ? left : right;
            if (index.aIndex !== undefined) {
                forEachSliceRanges(leftRanges, index.aIndex, index.aIndex + index.length, result.length, addRange);
            }
            if (index.bIndex !== undefined) {
                forEachSliceRanges(rightRanges, index.bIndex, index.bIndex + index.length, result.length, addRange);
            }
            if (index.oIndex !== undefined) {
                forEachSliceRanges(baseRanges, index.oIndex, index.oIndex + index.length, result.length, addRange);
            }
            result += str.slice(start, end);
        }
    }
    return { text: result, ranges };
}
function diff3MergeStrings(base, left, right) {
    return diff3MergeStringRanges(base, left, right).text;
}

function trimergeString(orig = '', left = '', right = '') {
    if (typeof orig !== 'string' ||
        typeof left !== 'string' ||
        typeof right !== 'string') {
        return CannotMerge;
    }
    return diff3MergeStrings(orig, left, right);
}

function trimergeArrayCreator(getArrayItemKey, allowOrderConflicts = false) {
    return (orig, left, right, path, mergeFn) => {
        if (jsSameType(orig, left, right) !== 'array') {
            return CannotMerge;
        }
        function arrayToMap(array) {
            const map = new Map();
            array.forEach((item, index) => {
                const key = getArrayItemKey(item, index, path);
                if (map.has(key)) {
                    throw new Error(`Duplicate array key '${key}' at /${path}`);
                }
                map.set(key, item);
            });
            return map;
        }
        const origMap = arrayToMap(orig);
        const leftMap = arrayToMap(left);
        const rightMap = arrayToMap(right);
        const mergedArray = [];
        switch (internalTrimergeOrderedMap(origMap, leftMap, rightMap, path, mergeFn, allowOrderConflicts, (_key, merged) => mergedArray.push(merged))) {
            case 'left':
                return left;
            case 'right':
                return right;
            default:
                return mergedArray;
        }
    };
}

const trimergeJsonDeepEqual = trimergeEqualityCreator(jsonEqual);

export { CannotMerge, CannotMergeError, RouteWildCard, combineMergers, diff3Keys, diff3MergeIndices, diff3MergeStringRanges, diffIndices, jsonEqual, routeMergers, trimergeArrayCreator, trimergeEquality, trimergeEqualityCreator, trimergeJsonDeepEqual, trimergeObject as trimergeJsonObject, trimergeMap, trimergeMapCreator, trimergeObject, trimergeString, trimergeUnorderedMap };
